import {
	type OverdueVehicleUsage,
	VEHICLE_USAGE_TYPE,
} from "../../types/vehicle-usage.types";
import { toVehicleReference } from "./to-vehicle-reference";
import type { AdjacentEvents } from "@/features/bookings/components/adjacent-vehicle-events-accordion/utils/get-adjacent-events";
import type { NonAvailability } from "@/features/scheduler/types/availability-event.types";
import {
	type AdjacentEventsDto,
	type BookingReferenceDto,
	BookingType,
	type NonAvailabilityDto,
	type OverdueVehicleUsageDto,
} from "@/lib/api";
import { toPeriod } from "@/utils/mappers/to-period";
import { withImageVersion } from "@/utils/with-image-version/with-image-version";

/**
 * A usage is a private usage (for the Type chip) when it has no booking or its
 * booking is SYNTHETIC (private-use). Note this is intentionally distinct from
 * {@link OverdueVehicleUsage.isPrivateUsage}, which is true only when there is
 * no booking at all and drives whether the row is clickable.
 */
function isPrivateUsageType(booking: BookingReferenceDto | undefined): boolean {
	return !booking || booking.bookingType === BookingType.SYNTHETIC;
}

// Fleet API dates carry no `[UTC]` suffix, so no cleaning is needed here.
function toNonAvailability(dto: NonAvailabilityDto): NonAvailability {
	return {
		availabilityId: dto.availabilityId,
		recurringAvailabilityId: dto.recurringAvailabilityId,
		vehicleId: dto.vehicleId,
		type: dto.type as NonAvailability["type"],
		booking: dto.booking
			? {
					id: dto.booking.id,
					user: dto.booking.user
						? { displayName: dto.booking.user.displayName }
						: undefined,
					status: dto.booking.status,
					overdue: dto.booking.overdue,
					comments: dto.booking.comments,
				}
			: undefined,
		plannedPeriod: toPeriod(dto.plannedPeriod),
		usagePeriod: toPeriod(dto.usagePeriod),
		overdue: dto.overdue,
		serviceUnavailability: dto.serviceUnavailability,
		comments: dto.comments,
	};
}

function toAdjacentEvents(
	dto: AdjacentEventsDto | undefined
): AdjacentEvents | undefined {
	if (!dto) return undefined;

	return {
		previous: dto.previous ? toNonAvailability(dto.previous) : undefined,
		current: dto.current ? toNonAvailability(dto.current) : undefined,
		next: dto.next ? toNonAvailability(dto.next) : undefined,
	};
}

/**
 * Maps a single overdue vehicle usage from the
 * `GET /fleet/vehicle-usage/v1/overdue` response to the domain row rendered by
 * the Overdue Vehicle Usages table.
 *
 * Module-level named function (stable reference) — see `architecture-warnings.md`.
 */
export function toOverdueVehicleUsage(
	dto: OverdueVehicleUsageDto
): OverdueVehicleUsage {
	const { vehicleUsage, vehicle, user, booking } = dto;

	return {
		id: vehicleUsage?.id,
		// Only navigate when there is a real booking (private usages: no-op).
		bookingId: booking?.id,
		isPrivateUsage: booking == null,
		usageType: isPrivateUsageType(booking)
			? VEHICLE_USAGE_TYPE.PRIVATE_USAGE
			: VEHICLE_USAGE_TYPE.BOOKING,
		vehicle: vehicle ? toVehicleReference(vehicle) : undefined,
		driver: user
			? {
					remoteId: user.remoteId,
					userName: user.userName,
					displayName: user.displayName,
					imageUrl: withImageVersion(user.imageUrl),
				}
			: undefined,
		client: booking?.client
			? { id: booking.client.id, name: booking.client.name }
			: undefined,
		status: vehicleUsage?.status,
		plannedEnd: booking?.plannedPeriod.end ?? vehicleUsage?.period?.end,
		period: toPeriod(vehicleUsage?.period),
		batteryPercentage: dto.batteryPercentage,
		distanceFromHomePosition: dto.distanceFromHomePosition,
		adjacentEvents: toAdjacentEvents(dto.adjacentEvents),
	};
}
